// src/careComponents/components/InstallmentsInline.tsx
import * as React from "react";
import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { GET_INSTALLMENTS_BY_LEAD } from "../../graphql/queries/care";

function fmtUSD(n: number) {
  try {
    return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
  } catch {
    return `$${(Math.round(n * 100) / 100).toFixed(2)}`;
  }
}

type InstallmentStatus = "DUE" | "PAID" | "CANCELLED" | "FAILED";

type Installment = {
  id: string;
  leadId: number;
  sequence: number;     // 1-based order
  dueDate: string;      // DATEONLY "YYYY-MM-DD" or ISO
  dueAmount: number;    // DECIMAL(12,2)
  paidAmount: number;   // DECIMAL(12,2)
  paidAt?: string | null;
  status: InstallmentStatus;
};

export default function InstallmentsInline({
  leadId,
  onBeginPay,
}: {
  leadId: number | string;
  onBeginPay?: (p: { installmentId: string; amountDue: number }) => void;
}) {
  const { data, loading, error } = useQuery(GET_INSTALLMENTS_BY_LEAD, {
    variables: { leadId: String(leadId) },
    fetchPolicy: "cache-and-network",
  });

  if (loading) return <LinearProgress sx={{ my: 1 }} />;
  if (error)
    return (
      <Alert severity="error">
        {String((error as any)?.message || error)}
      </Alert>
    );

  const rows: Installment[] = (data?.referralInstallments ?? []) as Installment[];
  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No installments.
      </Typography>
    );
  }

  // ----- derive values (no hooks) -----
  // never mutate Apollo arrays: clone before sort
  const ordered = [...rows].sort(
    (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
  );

  const total = ordered.reduce((s, r) => s + Number(r.dueAmount || 0), 0);
  const paid = ordered.reduce((s, r) => s + Number(r.paidAmount || 0), 0);
  const remaining = Math.max(0, total - paid);

  // Next due = first row with outstanding balance and not cancelled/failed/paid
  const next = ordered.find((r) => {
    const left = Math.max(0, Number(r.dueAmount) - Number(r.paidAmount || 0));
    if (left <= 0) return false;
    return r.status !== "PAID" && r.status !== "CANCELLED" && r.status !== "FAILED";
  });

  const amountDueNext = next
    ? Math.max(0, Number(next.dueAmount) - Number(next.paidAmount || 0))
    : 0;

  const statusColor = (s: InstallmentStatus) => {
    switch (s) {
      case "PAID":
        return "success";
      case "DUE":
        return "warning";
      case "FAILED":
        return "error";
      case "CANCELLED":
      default:
        return "default";
    }
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 1.25,
      }}
    >
      {/* Summary row */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Chip size="small" variant="outlined" label={`Total: ${fmtUSD(total)}`} />
        <Chip
          size="small"
          color="success"
          variant="outlined"
          label={`Paid: ${fmtUSD(paid)}`}
        />
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label={`Remaining: ${fmtUSD(remaining)}`}
        />
        <Box sx={{ flex: 1 }} />
        {next && onBeginPay && amountDueNext > 0 && (
          <Button
            size="small"
            variant="contained"
            onClick={() =>
              onBeginPay({ installmentId: next.id, amountDue: amountDueNext })
            }
          >
            Pay {fmtUSD(amountDueNext)} now
          </Button>
        )}
      </Stack>

      <Divider sx={{ mb: 1 }} />

      {/* List */}
      <Stack spacing={0.75}>
        {ordered.map((r) => {
          const due = Number(r.dueAmount || 0);
          const paidAmt = Number(r.paidAmount || 0);
          const remainingThis = Math.max(0, due - paidAmt);

          const d = new Date(r.dueDate);
          const dueStr = Number.isNaN(d.getTime())
            ? r.dueDate
            : d.toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "2-digit",
              });

          const canPayThis =
            !!onBeginPay &&
            remainingThis > 0 &&
            r.status !== "PAID" &&
            r.status !== "CANCELLED" &&
            r.status !== "FAILED";

          return (
            <Stack
              key={r.id}
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ minHeight: 32 }}
            >
              <Chip size="small" variant="outlined" label={`#${r.sequence}`} />
              <Typography variant="body2" sx={{ minWidth: 120 }}>
                {dueStr}
              </Typography>
              <Typography variant="body2" sx={{ minWidth: 140 }}>
                Due: <strong>{fmtUSD(due)}</strong>
              </Typography>
              <Typography variant="body2" sx={{ minWidth: 140 }}>
                Paid: {fmtUSD(paidAmt)}
              </Typography>
              <Typography variant="body2" sx={{ minWidth: 150 }}>
                Remaining: {fmtUSD(remainingThis)}
              </Typography>
              <Chip
                size="small"
                color={
                  statusColor(r.status) as
                    | "default"
                    | "success"
                    | "warning"
                    | "error"
                }
                variant="outlined"
                label={r.status}
              />
              <Box sx={{ flex: 1 }} />
              {canPayThis && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() =>
                    onBeginPay!({ installmentId: r.id, amountDue: remainingThis })
                  }
                >
                  Pay this
                </Button>
              )}
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}
